// src/context/HelperOnboardingContext.js
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";

const HelperOnboardingContext = createContext();

const buildInitialDraft = (overrides = {}) => {
  const timezone =
    typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC";

  return {
    step: overrides.step || 1,
    completedInitialSetup: overrides.completedInitialSetup || false,
    basicInfo: {
      name: "",
      primarySkills: [],
      ...overrides.basicInfo,
    },
    experience: {
      years: "",
      portfolioLinks: [],
      ...overrides.experience,
    },
    availability: {
      timezone,
      availabilityStatus: "available",
      ...overrides.availability,
    },
    advancedDetails: {
      hourlyRate: "",
      extendedBio: "",
      certifications: [],
      ...overrides.advancedDetails,
    },
  };
};

export const HelperOnboardingProvider = ({ children }) => {
  const { currentUser, currentUserRole, ready } = useAuth();
  const [draft, setDraft] = useState(null);
  const [helperRecord, setHelperRecord] = useState(null);
  // Starts true so that guards depending on onboarding state cannot read
  // "onboarding incomplete" from the empty initial state. Before this defaulted
  // to false, an onboarded expert opening a workspace link directly was
  // redirected to /onboarding on the first render, because the redirect was
  // evaluated before these listeners had even attached.
  const [loadingDraft, setLoadingDraft] = useState(true);
  const bootstrapAttemptedRef = useRef(false);

  const bootstrapDraft = useCallback(async () => {
    if (!currentUser) return;

    try {
      const draftRef = doc(db, "helpersDraft", currentUser.uid);
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};

      const initialDraft = buildInitialDraft({
        basicInfo: {
          name: userData.name || currentUser.displayName || "",
          primarySkills: userData.skills || [],
        },
        experience: userData.experience || {},
        availability: {
          timezone: userData.location?.timezone,
          availabilityStatus: userData.availability?.availabilityStatus,
        },
        advancedDetails: userData.advancedDetails || {},
        step: userData.onboardingStep || 1,
        completedInitialSetup: userData.completedInitialSetup || false,
      });

      await setDoc(
        draftRef,
        {
          ...initialDraft,
          step: initialDraft.step || 1,
          completedInitialSetup: initialDraft.completedInitialSetup || false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Failed to bootstrap helper draft:", error);
    }
  }, [currentUser]);

  useEffect(() => {
    bootstrapAttemptedRef.current = false;
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!ready || !currentUser || normalizeRole(currentUserRole) !== ROLE_VALUE.specialist) {
      setDraft(null);
      setHelperRecord(null);
      setLoadingDraft(false);
      return;
    }

    setLoadingDraft(true);
    const draftRef = doc(db, "helpersDraft", currentUser.uid);
    const helperRef = doc(db, "helpers", currentUser.uid);

    // Onboarding completion can be recorded on either document, so the state is
    // only trustworthy once both listeners have reported. Clearing the flag on
    // the first one to answer would let a guard act on half the picture.
    let draftReported = false;
    let helperReported = false;
    const settle = (which) => {
      if (which === "draft") draftReported = true;
      if (which === "helper") helperReported = true;
      if (draftReported && helperReported) setLoadingDraft(false);
    };

    const unsubscribeDraft = onSnapshot(
      draftRef,
      (snap) => {
        if (snap.exists()) {
          setDraft({ id: snap.id, ...snap.data() });
        } else {
          setDraft(null);
          if (!bootstrapAttemptedRef.current) {
            bootstrapAttemptedRef.current = true;
            bootstrapDraft();
          }
        }
        settle("draft");
      },
      (error) => {
        console.error("helpersDraft listener error:", error);
        settle("draft");
      }
    );

    const unsubscribeHelper = onSnapshot(
      helperRef,
      (snap) => {
        if (snap.exists()) {
          setHelperRecord({ id: snap.id, ...snap.data() });
        } else {
          setHelperRecord(null);
        }
        settle("helper");
      },
      (error) => {
        console.error("helpers listener error:", error);
        settle("helper");
      }
    );

    return () => {
      unsubscribeDraft();
      unsubscribeHelper();
    };
  }, [ready, currentUser, currentUserRole, bootstrapDraft]);

  // Check if Basic Info (Step 1) is complete
  const basicInfoComplete = Boolean(
    helperRecord?.basicInfo?.name?.trim() &&
      Array.isArray(helperRecord?.basicInfo?.primarySkills) &&
      helperRecord.basicInfo.primarySkills.length > 0
  );

  // Check if Experience (Step 2) is complete
  const experienceComplete = Boolean(
    helperRecord?.experience?.years &&
      Number(helperRecord.experience.years) >= 0
  );

  // Check if Availability (Step 3) is complete
  const availabilityComplete = Boolean(
    helperRecord?.availability?.timezone?.trim() &&
      helperRecord?.availability?.availabilityStatus &&
      helperRecord?.availability?.continent &&
      helperRecord?.availability?.country
  );

  // Treat onboarding as complete if explicitly marked or all sections are filled
  const completedInitialSetup = Boolean(
    helperRecord?.completedInitialSetup || draft?.completedInitialSetup
  );

  // Require onboarding only when we have the data loaded and it's incomplete
  const requiresOnboarding = Boolean(
    ready &&
    !loadingDraft &&
    normalizeRole(currentUserRole) === ROLE_VALUE.specialist &&
    !completedInitialSetup &&
    (!basicInfoComplete || !experienceComplete || !availabilityComplete)
  );

  return (
    <HelperOnboardingContext.Provider
      value={{
        draft,
        helperRecord,
        loadingDraft,
        completedInitialSetup,
        requiresOnboarding,
        basicInfoComplete,
        bootstrapDraft,
      }}
    >
      {children}
    </HelperOnboardingContext.Provider>
  );
};

export const useHelperOnboarding = () => {
  const context = useContext(HelperOnboardingContext);
  if (context === undefined) {
    throw new Error(
      "useHelperOnboarding must be used within a HelperOnboardingProvider"
    );
  }
  return context;
};


